import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import NombaseWordmark from "@/components/NombaseWordmark";
import LogoutPage from "@/pages/LogoutPage";

// Lazy-load every route. Each becomes a separate webpack chunk so the
// initial bundle only contains the chrome shell + the chunk for the
// route the user actually landed on.
// `webpackPrefetch` on AuthPage tells the browser to fetch the chunk
// during idle time on every page, including /sign-out — so when the
// post-logout redirect lands on /sign-in, the chunk is already warm
// in the HTTP cache. Cuts sign-out → sign-in latency materially.
const AuthPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/AuthPage"));
const PendingSetup = lazy(() => import("@/pages/PendingSetup"));
const PostIntakePage = lazy(() => import("@/pages/PostIntakePage"));

/**
 * Shell fallback — paints in <50ms, exactly matches the AuthPage chrome
 * (background, dot grid, centered wordmark) so the user sees a stable
 * frame the moment React mounts. Then the route chunk swaps in.
 */
function RouteShell() {
  return (
    <div
      className="min-h-[100dvh] w-full bg-[#F7F8FA] relative flex flex-col items-center justify-start"
      data-testid="route-shell"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #0F172A 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="relative z-10 pt-6 sm:pt-8 px-6">
        <NombaseWordmark size="sm" variant="light" />
      </div>
    </div>
  );
}

function RootRedirect() {
  // Honor legacy `?tab=signup` so old bookmarks/email links still land
  // on the signup page; otherwise default to sign-in.
  const params = new URLSearchParams(window.location.search);
  const target = params.get("tab") === "signup" ? "/sign-up" : "/sign-in";
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <div
      className="min-h-screen bg-[#F7F8FA] text-[#0F172A]"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="app-root"
    >
      <Suspense fallback={<RouteShell />}>
        <Routes>
          {/* Canonical clean URLs */}
          <Route path="/sign-in" element={<AuthPage tab="signin" />} />
          <Route path="/sign-up" element={<AuthPage tab="signup" />} />
          {/* Root redirects to sign-in (or sign-up if legacy ?tab=signup) */}
          <Route path="/" element={<RootRedirect />} />
          {/* Legacy paths — redirect to canonical */}
          <Route path="/login/*" element={<Navigate to="/sign-in" replace />} />
          <Route path="/signin/*" element={<Navigate to="/sign-in" replace />} />
          <Route path="/signup/*" element={<Navigate to="/sign-up" replace />} />
          <Route path="/register/*" element={<Navigate to="/sign-up" replace />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/sign-out" element={<LogoutPage />} />
          <Route path="/pending" element={<PendingSetup />} />
          <Route path="/post-intake" element={<PostIntakePage />} />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
